import React, { useState, useEffect, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Badge } from "@/components/ui/badge";
import { useDebounce } from "@/hooks/use-debounce";
import { useQueryClient } from "@tanstack/react-query";
import {
  useMedicineSearch,
  useAddPrescriptionItem,
  useMedicineUnits,
  useDoseForms,
} from "@/hooks/usePrescriptions";
import { invalidateQueries } from "@/lib/query-keys";
import { toast } from "sonner";
import { apiService, apiRequest } from "@/lib/api";
import {
  Plus,
  Search,
  Loader2,
  AlertCircle,
  ChevronLeft,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { BrandMaster } from "@/types/prescription";

interface AddMedicineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
}

type ViewState = "search" | "create-medicine";

// Types for Create Medicine
interface MedicineAPI {
  api_id: number;
  api_name: string;
  permitted: boolean;
}

interface ComboboxOption {
  label: string;
  value: string;
}

// Ingredient schema with validation
const ingredientSchema = z
  .object({
    apiId: z.number().optional(),
    apiName: z.string().min(1, "API name is required"),
    potency: z.string().min(1, "Potency is required"),
    unit: z.string().min(1, "Unit is required"),
  })
  .refine((data) => data.apiId && data.apiId > 0, {
    message: "API selection is required",
    path: ["apiName"],
  });

const medicineSchema = z.object({
  medicineName: z.string().min(1, "Medicine name is required"),
  doseForm: z.string().min(1, "Dose form is required"),
  dispensingUnit: z.string().min(1, "Dispensing unit is required"),
  ingredients: z
    .array(ingredientSchema)
    .min(1, "At least one ingredient is required"),
});

type MedicineFormValues = z.infer<typeof medicineSchema>;

// API Search Dialog Component
const APISearchDialog = ({
  open,
  onOpenChange,
  onSelect,
  selectedAPIs = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (apis: MedicineAPI[]) => void;
  selectedAPIs?: MedicineAPI[];
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MedicineAPI[]>([]);
  const [selected, setSelected] = useState<MedicineAPI[]>(selectedAPIs);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const prevOpenRef = React.useRef(open);

  // Reset state ONLY when dialog opens (transitions from closed to open)
  useEffect(() => {
    // Only reset when opening (previous was false, now is true)
    if (open && !prevOpenRef.current) {
      setSelected(selectedAPIs);
      setSearchQuery("");
      setSearchResults([]);
      setHasSearched(false);
      setShowCreateConfirm(false);
    }
    prevOpenRef.current = open;
  }, [open, selectedAPIs]);

  const searchAPIs = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    try {
      const response = await apiRequest<{ results: MedicineAPI[] }>(() =>
        apiService.searchAPIs(query)
      );

      const filteredResults = response.results.filter(
        (api) => !selected.some((sel) => sel.api_id === api.api_id)
      );
      setSearchResults(filteredResults);
    } catch (error) {
      console.error("Error searching APIs:", error);
      setSearchResults([]);
      toast.error("Failed to search APIs. Please check your connection.");
    } finally {
      setIsSearching(false);
    }
  };

  const createNewAPI = async (apiName: string) => {
    setIsCreating(true);
    try {
      await apiRequest<MedicineAPI>(() => apiService.createAPI(apiName));

      toast.success(`${apiName} was added successfully as a new API.`);

      await searchAPIs(apiName);
      setShowCreateConfirm(false);
    } catch (error) {
      console.error("Error creating API:", error);
      toast.error("Failed to create API. Please check your connection.");
    } finally {
      setIsCreating(false);
    }
  };

  const toggleAPISelection = (api: MedicineAPI) => {
    const isSelected = selected.some((sel) => sel.api_id === api.api_id);
    if (isSelected) {
      setSelected((prev) => prev.filter((sel) => sel.api_id !== api.api_id));
      if (
        searchQuery.length >= 2 &&
        api.api_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !searchResults.some((result) => result.api_id === api.api_id)
      ) {
        setSearchResults((prev) =>
          [...prev, api].sort((a, b) => a.api_name.localeCompare(b.api_name))
        );
      }
    } else {
      setSelected((prev) => [...prev, api]);
      setSearchResults((prev) =>
        prev.filter((result) => result.api_id !== api.api_id)
      );
      setSearchQuery("");
    }
    onSelect(
      isSelected
        ? selected.filter((sel) => sel.api_id !== api.api_id)
        : [...selected, api]
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => searchAPIs(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden border-border/60">
          <DialogHeader>
            <DialogTitle className="z-h2 text-foreground">
              Select Active Ingredients
            </DialogTitle>
            <DialogDescription className="z-body text-muted-foreground">
              Search and select active ingredients for your medicine.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {selected.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {selected.map((api) => (
                    <Badge
                      key={api.api_id}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => toggleAPISelection(api)}
                    >
                      {api.api_name}
                      <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="z-label text-muted-foreground">
                Search for active ingredients
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search for API"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="min-h-[200px] max-h-[200px] overflow-y-auto border rounded-lg p-4 bg-muted/30">
              {isSearching ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : hasSearched && searchResults.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full space-y-4">
                  <p className="text-muted-foreground">
                    No results found. Do you want to add new?
                  </p>
                  <Button
                    onClick={() => setShowCreateConfirm(true)}
                    disabled={isCreating}
                    size="sm"
                  >
                    {isCreating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    Add New
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {searchResults.map((api) => (
                    <Badge
                      key={api.api_id}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                      onClick={() => toggleAPISelection(api)}
                    >
                      {api.api_name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showCreateConfirm} onOpenChange={setShowCreateConfirm}>
        <AlertDialogContent className="border-border/60">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will add <strong>{searchQuery}</strong> as a new API in the
              database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                createNewAPI(searchQuery);
              }}
              disabled={isCreating}
            >
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Simple Select Component - Works reliably inside dialogs
const SimpleSelect = ({
  value,
  options,
  onChange,
  placeholder,
  className,
}: {
  value?: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}) => {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("w-full text-foreground", className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const AddMedicineDialog = ({
  open,
  onOpenChange,
  appointmentId,
}: AddMedicineDialogProps) => {
  const queryClient = useQueryClient();
  const [view, setView] = useState<ViewState>("search");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [apiSearchOpen, setApiSearchOpen] = useState(false);
  const [selectedIngredientIndex, setSelectedIngredientIndex] =
    useState<number>(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<{
    medicineName: string;
    doseForm: string;
    dispensingUnit: string;
    ingredients: Array<{
      apiId?: number;
      apiName: string;
      potency: string;
      unit: string;
    }>;
  } | null>(null);

  const { data: searchResults, isLoading: isSearching } =
    useMedicineSearch(debouncedSearch);
  const { data: units } = useMedicineUnits();
  const { data: doseForms } = useDoseForms();
  const addPrescriptionMutation = useAddPrescriptionItem(appointmentId);

  const defaultValues: MedicineFormValues = useMemo(
    () => ({
      medicineName: "",
      doseForm: "",
      dispensingUnit: "",
      ingredients: [{ apiId: 0, apiName: "", potency: "", unit: "" }],
    }),
    []
  );

  const form = useForm<MedicineFormValues>({
    resolver: zodResolver(medicineSchema),
    defaultValues,
    mode: "onSubmit",
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "ingredients",
  });

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset ALL states to prevent overlay issues
      setView("search");
      setSearchQuery("");
      setApiSearchOpen(false);
      setReviewOpen(false);
      setPendingSubmitData(null);
      setIsSubmitting(false);
      setSelectedIngredientIndex(0);
      form.reset(defaultValues);

      // Force cleanup of any lingering Radix scroll lock styles after dialog closes
      // Run multiple cleanup passes to catch any late Radix pointer-events changes
      const cleanupTimeout1 = setTimeout(() => {
        document.body.style.pointerEvents = "auto";
        document.body.style.overflow = "";
        document.body.removeAttribute("data-scroll-locked");
      }, 100);

      const cleanupTimeout2 = setTimeout(() => {
        // Second pass - sometimes Radix applies styles after the first cleanup
        document.body.style.pointerEvents = "auto";
        document.body.style.overflow = "";
        document.body.removeAttribute("data-scroll-locked");
        // Then reset to normal
        setTimeout(() => {
          document.body.style.pointerEvents = "";
        }, 50);
      }, 300);

      return () => {
        clearTimeout(cleanupTimeout1);
        clearTimeout(cleanupTimeout2);
      };
    }
  }, [open, form, defaultValues]);

  const handleSelectMedicine = async (medicine: BrandMaster) => {
    try {
      await addPrescriptionMutation.mutateAsync({
        medicine_brand: medicine.brand_id,
        quantity: 0,
        medicine_unit: undefined,
        medicine_frequency: undefined,
        medicine_duration: undefined,
        medicine_advice: undefined,
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to add prescription item:", error);
      toast.error("Failed to add medicine to prescription.");
    }
  };

  const handleAPISelect = (apis: MedicineAPI[]) => {
    const currentIngredients = form.getValues("ingredients");

    if (apis.length === 0) {
      update(selectedIngredientIndex, {
        apiId: 0,
        apiName: "",
        potency: currentIngredients[selectedIngredientIndex]?.potency || "",
        unit: currentIngredients[selectedIngredientIndex]?.unit || "",
      });
    } else if (apis.length === 1) {
      const api = apis[0];
      update(selectedIngredientIndex, {
        apiId: api.api_id,
        apiName: api.api_name,
        potency: currentIngredients[selectedIngredientIndex]?.potency || "",
        unit: currentIngredients[selectedIngredientIndex]?.unit || "",
      });
    } else {
      const firstAPI = apis[0];
      update(selectedIngredientIndex, {
        apiId: firstAPI.api_id,
        apiName: firstAPI.api_name,
        potency: currentIngredients[selectedIngredientIndex]?.potency || "",
        unit: currentIngredients[selectedIngredientIndex]?.unit || "",
      });

      for (let i = 1; i < apis.length; i++) {
        const api = apis[i];
        append({
          apiId: api.api_id,
          apiName: api.api_name,
          potency: "",
          unit: "",
        });
      }
    }
  };

  const handleFormSubmit = () => {
    // Capture form values immediately when opening review dialog
    const values = form.getValues();
    setPendingSubmitData({
      medicineName: values.medicineName || "",
      doseForm: values.doseForm || "",
      dispensingUnit: values.dispensingUnit || "",
      ingredients: values.ingredients.map((ing) => ({
        apiId: ing.apiId || 0,
        apiName: ing.apiName || "",
        potency: ing.potency || "",
        unit: ing.unit || "",
      })),
    });
    setReviewOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingSubmitData) {
      console.error("No pending data to submit");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: pendingSubmitData.medicineName,
        medicine_unit: pendingSubmitData.dispensingUnit
          ? pendingSubmitData.dispensingUnit.toLowerCase()
          : "",
        dose_form: pendingSubmitData.doseForm || "",
        api: pendingSubmitData.ingredients.map((ing) => ({
          id: ing.apiId || 0,
          name: ing.apiName || "",
          potency: ing.potency || "",
          unit: ing.unit ? ing.unit.toLowerCase() : "",
        })),
      };
      console.log("Payload:", payload);

      const createdMedicine = await apiRequest<BrandMaster>(() =>
        apiService.addMedicine(payload)
      );

      // After creating, add to prescription
      await addPrescriptionMutation.mutateAsync({
        medicine_brand: createdMedicine.brand_id,
        quantity: 0,
        medicine_unit: undefined,
        medicine_frequency: undefined,
        medicine_duration: undefined,
        medicine_advice: undefined,
      });

      // Success - close the dialogs
      setIsSubmitting(false);

      // Invalidate medicine search cache so the new medicine appears immediately
      queryClient.invalidateQueries(invalidateQueries.medicines());

      // Show success toast
      toast.success("Medicine created and added to prescription!");

      // Close nested dialogs first in reverse order (inner to outer)
      setReviewOpen(false);
      setApiSearchOpen(false);
      setPendingSubmitData(null);

      // Small delay to let Radix cleanup the AlertDialog before closing main dialog
      setTimeout(() => {
        onOpenChange(false);

        // Force cleanup of Radix scroll lock with longer delay to ensure
        // all Radix cleanup cycles have completed
        setTimeout(() => {
          // Use 'auto' explicitly instead of clearing - this overrides any stuck styles
          document.body.style.pointerEvents = "auto";
          document.body.style.overflow = "";
          document.body.removeAttribute("data-scroll-locked");

          // After a moment, remove the explicit 'auto' to restore normal behavior
          setTimeout(() => {
            document.body.style.pointerEvents = "";
          }, 100);
        }, 200);
      }, 50);
    } catch (error) {
      console.error("Error creating medicine:", error);
      toast.error("Failed to create medicine. Please try again.");
      setIsSubmitting(false);
    }
  };

  const doseFormOptions: ComboboxOption[] = useMemo(
    () =>
      doseForms?.map((df) => ({
        label: df.dose_form_name,
        value: df.dose_form_name,
      })) || [],
    [doseForms]
  );

  const unitOptions: ComboboxOption[] = useMemo(
    () =>
      units?.map((u) => ({
        label: u.unit_name,
        value: u.unit_short_code,
      })) || [],
    [units]
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-border/60">
          <DialogHeader>
            <div className="flex items-center gap-2">
              {view !== "search" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-xl -ml-2"
                  onClick={() => setView("search")}
                >
                  <ChevronLeft className="size-4" />
                </Button>
              )}
              <DialogTitle className="z-h2 text-foreground">
                {view === "search" && "Add Medicine"}
                {view === "create-medicine" && "Add New Medicine"}
              </DialogTitle>
            </div>
          </DialogHeader>

          {view === "search" && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search medicine by brand or generic name..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="min-h-[300px]">
                {isSearching ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Searching...
                  </div>
                ) : searchResults?.results &&
                  searchResults.results.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults.results.map((medicine) => (
                      <div
                        key={medicine.brand_id}
                        className="p-3 rounded-xl border border-border/60 hover:bg-muted/60 cursor-pointer transition-colors"
                        onClick={() => handleSelectMedicine(medicine)}
                      >
                        <div className="z-body-md text-foreground">
                          {medicine.brand_name}
                        </div>
                        <div className="z-caption text-muted-foreground">
                          {medicine.composition}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : searchQuery.length >= 2 ? (
                  <div className="text-center py-8">
                    <div className="z-body text-muted-foreground mb-4">
                      No medicines found matching "{searchQuery}"
                    </div>
                    <Button
                      variant="outline"
                      className="text-foreground border-border hover:bg-muted"
                      onClick={() => {
                        form.setValue("medicineName", searchQuery);
                        setView("create-medicine");
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Medicine
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12 z-body text-muted-foreground">
                    Type at least 2 characters to search
                  </div>
                )}
              </div>
            </div>
          )}

          {view === "create-medicine" && (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleFormSubmit)}
                className="space-y-4"
              >
                <div className="bg-primary/10 border border-primary/20 p-3 rounded-lg text-sm text-primary flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <div>
                    You are adding a new medicine to the master database. Please
                    ensure details are correct.
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="medicineName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Brand Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Medicine Brand Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="doseForm"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dose Form *</FormLabel>
                        <FormControl>
                          <SimpleSelect
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select Form"
                            options={doseFormOptions}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dispensingUnit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dispensing Unit *</FormLabel>
                        <FormControl>
                          <SimpleSelect
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select Unit"
                            options={unitOptions}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base">Active Ingredient/s *</Label>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-12 gap-4 px-2">
                      <div className="col-span-5 z-label text-muted-foreground">
                        API Name
                      </div>
                      <div className="col-span-3 z-label text-muted-foreground">
                        Potency
                      </div>
                      <div className="col-span-3 z-label text-muted-foreground">
                        Unit
                      </div>
                      <div className="col-span-1"></div>
                    </div>

                    {fields.map((fieldItem, index) => (
                      <div
                        key={fieldItem.id}
                        className="grid grid-cols-12 gap-4 items-start bg-muted/50 p-3 rounded-xl border border-border/60"
                      >
                        <div className="col-span-5">
                          <FormField
                            control={form.control}
                            name={`ingredients.${index}.apiName`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className={cn(
                                      "w-full justify-start text-left font-normal text-xs bg-background text-foreground",
                                      !field.value && "text-muted-foreground",
                                      form.formState.errors.ingredients?.[index]
                                        ?.apiName && "border-destructive"
                                    )}
                                    onClick={() => {
                                      setSelectedIngredientIndex(index);
                                      setApiSearchOpen(true);
                                    }}
                                  >
                                    <Search className="mr-1 h-3 w-3 shrink-0" />
                                    <span className="text-foreground">
                                      {field.value || "Search API"}
                                    </span>
                                  </Button>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="col-span-3">
                          <FormField
                            control={form.control}
                            name={`ingredients.${index}.potency`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    placeholder="e.g. 500"
                                    className="bg-background text-xs"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="col-span-3">
                          <FormField
                            control={form.control}
                            name={`ingredients.${index}.unit`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <SimpleSelect
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Unit"
                                    className="bg-background text-xs"
                                    options={unitOptions}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="col-span-1 flex justify-center pt-2">
                          {fields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-muted-foreground hover:text-destructive transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        append({ apiId: 0, apiName: "", potency: "", unit: "" })
                      }
                      className="w-full border-dashed border-border bg-muted/50 hover:bg-muted text-muted-foreground"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add API
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border/60">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setView("search")}
                    className="text-foreground"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="">
                    Review & Create
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* API Search Dialog */}
      <APISearchDialog
        open={apiSearchOpen}
        onOpenChange={setApiSearchOpen}
        onSelect={handleAPISelect}
        selectedAPIs={
          fields[selectedIngredientIndex]
            ? [
                {
                  api_id: fields[selectedIngredientIndex].apiId || 0,
                  api_name: fields[selectedIngredientIndex].apiName || "",
                  permitted: true,
                },
              ].filter((api) => api.api_id > 0)
            : []
        }
      />

      {/* Confirmation Dialog - Using Dialog instead of AlertDialog for better control */}
      <Dialog
        open={reviewOpen}
        onOpenChange={(open) => !isSubmitting && setReviewOpen(open)}
      >
        <DialogContent className="border-border/60">
          <DialogHeader>
            <DialogTitle className="z-h2 text-foreground">
              Confirm New Medicine Entry
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-foreground">
                <div>
                  <span className="font-semibold">
                    {pendingSubmitData?.medicineName}
                  </span>
                  {pendingSubmitData?.doseForm && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({pendingSubmitData.doseForm})
                    </span>
                  )}
                </div>
                <div className="z-label text-muted-foreground">
                  Composition:
                </div>
                <div className="space-y-1">
                  {pendingSubmitData?.ingredients
                    .filter((ing) => ing.apiName && ing.potency && ing.unit)
                    .map((ing, idx) => (
                      <div key={idx} className="z-body">
                        {idx + 1}. {ing.apiName} - {ing.potency} {ing.unit}
                      </div>
                    ))}
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setReviewOpen(false)}
              disabled={isSubmitting}
              className="text-foreground"
            >
              Edit Details
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
              className="bg-success hover:bg-success text-white"
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm & Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
